import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { getConsent, saveConsent, syncRoute, track, trackPageView } from "../../lib/analytics";

const btn = "inline-flex min-h-[44px] items-center justify-center rounded-sm px-4 text-sm font-bold transition-colors";

// Fires one manual page_view per public route change (consent-gated inside trackPageView).
export const PageViewTracker = () => {
  const loc = useLocation();
  useEffect(() => {
    const onClick = (ev) => {
      const a = ev.target.closest?.("a[href^='http']");
      if (!a || a.hasAttribute("data-bp-share")) return;
      try {
        const host = new URL(a.href).hostname;
        if (host !== window.location.hostname) track("outbound_link_click", { domain: host.toLowerCase() });
      } catch {
        /* ignore malformed links */
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  useEffect(() => {
    syncRoute(loc.pathname);
    const t = setTimeout(() => trackPageView(loc.pathname + loc.search), 300);
    return () => clearTimeout(t);
  }, [loc.pathname, loc.search]);
  return null;
};

const Row = ({ title, desc, checked, onChange, disabled, testId }) => (
  <label className="flex items-start justify-between gap-4 border-t border-border py-3">
    <span><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="mt-1 h-5 w-5 accent-[#F04B2D]" data-testid={testId} />
  </label>
);

export const ConsentBanner = () => {
  const loc = useLocation();
  const [open, setOpen] = useState(() => !getConsent());
  const [custom, setCustom] = useState(false);
  const [c, setC] = useState(() => getConsent() || { analytics: false, ads: false });
  useEffect(() => {
    const h = () => { setC(getConsent() || { analytics: false, ads: false }); setCustom(true); setOpen(true); };
    window.addEventListener("bp-open-consent", h);
    return () => window.removeEventListener("bp-open-consent", h);
  }, []);
  if (!open || loc.pathname.startsWith("/admin")) return null;
  const done = (choice) => { saveConsent(choice); setOpen(false); setCustom(false); };
  return (
    <div role="dialog" aria-modal="false" aria-labelledby="consent-title" className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-card shadow-[0_-8px_30px_rgba(11,30,60,0.15)] sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md sm:rounded-sm sm:border" data-testid="consent-banner">
      <div className="max-h-[80vh] overflow-y-auto p-5">
        <p id="consent-title" className="flex items-center gap-2 font-display text-lg font-semibold"><Cookie className="h-5 w-5 text-pulse" /> Privatnost i kolačići</p>
        <p className="mt-2 text-[13.5px] text-muted-foreground">Uz tvoju saglasnost koristimo Google Analytics 4 i vlastito mjerenje BalkanPuls.de da razumijemo koje teme čitaoci posjećuju. Bez saglasnosti ne učitavamo analitiku i ne šaljemo događaje. Odluku možeš promijeniti u svakom trenutku u podnožju stranice. <Link to="/datenschutz" className="underline" lang="de">Datenschutz</Link></p>
        {custom && (
          <div className="mt-4" data-testid="consent-custom">
            <Row title="Neophodno" desc="Prijava, postavke prikaza i ova odluka. Uvijek aktivno." checked disabled testId="consent-necessary" />
            <Row title="Analitika (Google Analytics 4 i BalkanPuls mjerenje)" desc="Statistika posjeta i interakcija sa slučajnim identifikatorom preglednika; bez imena, emaila ili podataka profila." checked={c.analytics} onChange={(v) => setC({ ...c, analytics: v })} testId="consent-analytics-toggle" />
            <Row title="Oglašavanje" desc="Trenutno ne koristimo oglašavačke kolačiće. Ostaje isključeno dok to posebno ne odobriš." checked={c.ads} onChange={(v) => setC({ ...c, ads: v })} testId="consent-ads-toggle" />
          </div>
        )}
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <button onClick={() => done({ analytics: true, ads: false })} className={`${btn} bg-navy text-white hover:bg-navy-700 dark:bg-pulse`} data-testid="consent-accept-all">Prihvati sve</button>
          <button onClick={() => done({ analytics: false, ads: false })} className={`${btn} border border-border hover:border-ink`} data-testid="consent-reject">Odbij neobavezne</button>
          {custom ? (
            <button onClick={() => done(c)} className={`${btn} border border-pulse text-pulse sm:col-span-2`} data-testid="consent-save-custom">Spremi moj izbor</button>
          ) : (
            <button onClick={() => setCustom(true)} className={`${btn} text-ink underline sm:col-span-2`} data-testid="consent-customize">Prilagodi postavke</button>
          )}
        </div>
      </div>
    </div>
  );
};

export const openConsent = () => window.dispatchEvent(new Event("bp-open-consent"));
